import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import Login from '../../components/Login';
import { EmailVerification } from '../../components/EmailVerification';
import { AppShell } from '../../components/ui/AppShell';
import { surfaceShadows } from '../../surfaceStyles';
import { InventoryItemsPage } from '../items/InventoryItemsPage';
import type { BulkActionTransport } from '../bulk/useBulkActions';
import { usePhoneLayout } from '../../hooks/usePhoneLayout';
import { useUiPreferences } from '../../hooks/useUiPreferences';
import type { ItemSort } from '../collection/types';
import { InventoryDensityControl } from '../../components/ui/InventoryDensityControl';
import { ResponsiveDialog } from '../../components/ui/ResponsiveDialog';
import { DialogHeader } from '../../components/ui/DialogHeader';
import {
  AddIcon,
  SettingsIcon,
  SyncIcon,
  UsersIcon,
} from '../../components/ui/icons';
import ItemDetailsModal from '../../components/ItemDetailsModal';
import { LoadingState, SectionEmptyState } from '../../components/ui/States';
import { authService } from '../../services/authService';
import { AuthenticatedUser } from '../../domain/auth';
import { Item } from '../../domain/item';
import { createOperationId } from '../../domain/operationId';
import { imageOperationService } from '../../services/imageOperationService';
import { useNavigation } from '../navigation/NavigationProvider';
import { useFeedback } from '../feedback/FeedbackProvider';
import type {
  RecipientGrant,
  SharedItemsCursor,
  SharedViewScope,
  SharedViewResultMap,
} from '@qkindex/contracts';
import {
  readShared,
  SharedItem,
  sharedPath,
  sharedLocations,
} from './sharingService';
import { SharedItemMoveDialog } from './SharedItemMoveDialog';
import { SharedMoveDialog } from './SharedMoveDialog';
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard';

const sharedTitle = (share: RecipientGrant) =>
  `${share.collectionName}/${share.tagName}`;
const emptyTags = { tags: [], completeCollectionIds: [] };
const update = httpsCallable<Record<string, unknown>, unknown>(
  functions,
  'updateSharedViewItems',
);
const create = httpsCallable<Record<string, unknown>, unknown>(
  functions,
  'createSharedViewItem',
);
type Credential = { linkToken?: string; invitationToken?: string };
function readCredential(
  hash: string,
  storageKey: string,
  fallback: Credential = {},
): Credential {
  const fragment = new URLSearchParams(hash.slice(1));
  let value: Credential = {
    linkToken: fragment.get('link') || undefined,
    invitationToken: fragment.get('invitation') || undefined,
  };
  if (!value.linkToken && !value.invitationToken) {
    try {
      value =
        JSON.parse(window.sessionStorage.getItem(storageKey) || 'null') ||
        fallback;
    } catch {
      return fallback;
    }
  }
  return {
    ...(typeof value.linkToken === 'string'
      ? { linkToken: value.linkToken }
      : {}),
    ...(typeof value.invitationToken === 'string'
      ? { invitationToken: value.invitationToken }
      : {}),
  };
}

export function SharedWithMe() {
  const [shares, setShares] = useState<RecipientGrant[]>();
  const [error, setError] = useState('');
  const navigation = useNavigation();
  useEffect(() => {
    let active = true;
    void readShared('listMine', {})
      .then((result) => {
        if (active) setShares(result.shares);
      })
      .catch(() => {
        if (active) setError('Unable to load shared views.');
      });
    return () => {
      active = false;
    };
  }, []);
  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}
      {!shares && !error && <LoadingState label="Loading shared views…" />}
      {shares?.length === 0 && (
        <SectionEmptyState
          icon={UsersIcon}
          message="Nothing shared with you yet"
        />
      )}
      {shares?.map((share) => (
        <Button
          key={share.shareId}
          variant="outlined"
          onClick={() =>
            navigation.navigate(sharedPath(share.collectionId!, share.shareId))
          }
        >
          {sharedTitle(share)}
        </Button>
      ))}
    </Stack>
  );
}

export function SharedRoute() {
  const navigation = useNavigation();
  return <ScopedSharedRoute key={navigation.location.pathname} />;
}

function ScopedSharedRoute() {
  const navigation = useNavigation();
  const [user, setUser] = useState<AuthenticatedUser | null | undefined>(
    undefined,
  );
  useEffect(() => authService.subscribe(setUser), []);
  const refreshUser = useCallback(async () => {
    const refreshed = await authService.refreshCurrentUser();
    setUser(refreshed);
    return refreshed.emailVerified;
  }, []);
  const parts = navigation.location.pathname.split('/').filter(Boolean);
  let collectionId = '',
    shareId = '';
  try {
    collectionId = decodeURIComponent(parts[1] || '');
    shareId = decodeURIComponent(parts[2] || '');
  } catch {
    /* invalid route */
  }
  const credentialKey = `qkindex.sharedCredential:${collectionId}:${shareId}`;
  const [credential, setCredential] = useState(() =>
    readCredential(navigation.location.hash, credentialKey),
  );
  const { linkToken, invitationToken } = credential;
  useEffect(() => {
    let meta = document.querySelector<HTMLMetaElement>('meta[name="referrer"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'referrer';
      document.head.appendChild(meta);
    }
    meta.content = 'no-referrer';
    const nextCredential = readCredential(
      navigation.location.hash,
      credentialKey,
      credential,
    );
    if (
      nextCredential.linkToken !== credential.linkToken ||
      nextCredential.invitationToken !== credential.invitationToken
    )
      setCredential(nextCredential);
    try {
      window.sessionStorage.setItem(
        credentialKey,
        JSON.stringify(nextCredential),
      );
    } catch {
      /* in-memory credential still works */
    }
    if (navigation.location.hash)
      navigation.navigate(
        navigation.location.pathname + navigation.location.search,
        { replace: true },
      );
  }, [credentialKey, credential, navigation]);
  const scope = useMemo(
    () => ({ collectionId, shareId, ...(linkToken ? { linkToken } : {}) }),
    [collectionId, shareId, linkToken],
  );
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  if (
    !collectionId ||
    !shareId ||
    parts.length !== 3 ||
    collectionId.includes('/') ||
    shareId.includes('/')
  )
    return (
      <AppShell>
        <Alert severity="error">This sharing link is invalid.</Alert>
      </AppShell>
    );
  if (user === undefined || navigation.location.hash)
    return (
      <AppShell>
        <LoadingState label="Loading…" />
      </AppShell>
    );
  if (!user && (!linkToken || invitationToken)) return <Login />;
  if (invitationToken && user && !user.emailVerified)
    return (
      <EmailVerification
        email={user.email}
        onRefresh={refreshUser}
        onResend={authService.resendVerification}
        onSignOut={authService.signOut}
      />
    );
  if (invitationToken)
    return (
      <AppShell removeMargins flushTop>
        <Box
          component="main"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            px: { xs: 0.5, sm: 2 },
            pt: { xs: 4, sm: 'clamp(64px, 16vh, 180px)' },
            pb: 4,
          }}
        >
          <Stack
            component="section"
            aria-labelledby="shared-invitation-title"
            aria-busy={busy}
            spacing={3}
            sx={{
              width: '100%',
              maxWidth: 440,
              minWidth: 0,
              p: { xs: 3, sm: 4 },
              bgcolor: 'background.paper',
              borderRadius: '26px',
              boxShadow: surfaceShadows.control,
            }}
          >
            <Stack spacing={1.5}>
              <Typography
                id="shared-invitation-title"
                component="h1"
                variant="h5"
              >
                Shared inventory invitation
              </Typography>
              <Typography color="text.secondary">
                Accept with the verified email address that was invited.
              </Typography>
            </Stack>
            {user?.email && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Signed in as
                </Typography>
                <Typography sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
                  {user.email}
                </Typography>
              </Box>
            )}
            {error && <Alert severity="error">{error}</Alert>}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button
                variant="contained"
                disabled={busy}
                sx={{ flex: 1 }}
                onClick={() => {
                  setBusy(true);
                  void readShared('acceptInvitation', {
                    collectionId,
                    shareId,
                    invitationToken,
                  })
                    .then(() => {
                      setCredential({});
                      window.sessionStorage.removeItem(credentialKey);
                      navigation.navigate(sharedPath(collectionId, shareId), {
                        replace: true,
                      });
                    })
                    .catch((failure) =>
                      setError(
                        failure instanceof Error
                          ? failure.message
                          : 'Unable to accept invitation.',
                      ),
                    )
                    .finally(() => setBusy(false));
                }}
              >
                Accept invitation
              </Button>
              <Button
                disabled={busy}
                sx={{ flex: 1, boxShadow: 'none' }}
                onClick={() => {
                  setBusy(true);
                  void readShared('declineInvitation', {
                    collectionId,
                    shareId,
                    invitationToken,
                  })
                    .then(() => {
                      setCredential({});
                      window.sessionStorage.removeItem(credentialKey);
                      navigation.navigate('/items', { replace: true });
                    })
                    .catch(() => setError('Unable to decline invitation.'))
                    .finally(() => setBusy(false));
                }}
              >
                Decline
              </Button>
            </Stack>
            <Button
              disabled={busy}
              sx={{ alignSelf: 'center', fontSize: 13, boxShadow: 'none' }}
              onClick={() => {
                void authService.signOut();
              }}
            >
              Use another account
            </Button>
          </Stack>
        </Box>
      </AppShell>
    );
  return (
    <SharedInventory
      key={`${collectionId}:${shareId}:${linkToken || ''}:${user?.uid || 'anonymous'}:${user?.emailVerified}`}
      scope={scope}
      user={user}
      refreshUser={refreshUser}
    />
  );
}

export function SharedInventory({
  scope,
  user,
  refreshUser,
}: {
  scope: SharedViewScope;
  user: AuthenticatedUser | null;
  refreshUser(): Promise<boolean>;
}) {
  const [requiresSignIn, setRequiresSignIn] = useState(false);
  const { confirm } = useFeedback();
  const [view, setView] = useState<SharedViewResultMap['listItems']>();
  const [items, setItems] = useState<SharedItem[]>([]);
  const [cursor, setCursor] = useState<SharedItemsCursor>();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const phoneLayout = usePhoneLayout();
  const ui = useUiPreferences();
  const [sort, setSort] = useState<ItemSort>('keywords-asc');
  const [displayOpen, setDisplayOpen] = useState(false);
  const [selected, setSelected] = useState<SharedItem | null>(null);
  const selectedRef = useRef<SharedItem | null>(null);
  selectedRef.current = selected;
  const [adding, setAdding] = useState(false);
  const [keywords, setKeywords] = useState('');
  const [location, setLocation] = useState('');
  const [qty, setQty] = useState<number | string>('');
  const [image, setImage] = useState<File | null>(null);
  const [imageCleared, setImageCleared] = useState(false);
  const [imagePreview, setImagePreview] = useState<string>();
  const [locations, setLocations] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [saving, setSaving] = useState(false);
  const [moveSelection, setMoveSelection] = useState<{
    items: Item[];
    onComplete(): void;
  } | null>(null);
  const [moveItem, setMoveItem] = useState<'in' | null>(null);
  const alive = useRef(true);
  const epoch = useRef(0);
  const grantRevision = useRef<number | undefined>(undefined);
  const imageRequests = useRef(new Set<string>());
  const pendingMutation = useRef<
    { key: string; operationId: string } | undefined
  >(undefined);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      epoch.current += 1;
    };
  }, []);
  useEffect(() => {
    if (!image) {
      setImagePreview(undefined);
      return;
    }
    const url = URL.createObjectURL(image);
    setImagePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [image]);
  const refresh = useCallback(async () => {
    const generation = ++epoch.current;
    setLoading(true);
    try {
      const page = await readShared('listItems', scope);
      const activeItemId = selectedRef.current?.id;
      let activeItemIncluded =
        !activeItemId || page.items.some((item) => item.id === activeItemId);
      if (activeItemId && !activeItemIncluded) {
        try {
          await readShared('getItem', { ...scope, itemId: activeItemId });
          activeItemIncluded = true;
        } catch {
          /* Item is no longer accessible. */
        }
      }
      if (!alive.current || epoch.current !== generation) return;
      const grantChanged = grantRevision.current !== page.share.revision;
      if (grantRevision.current !== undefined && grantChanged) {
        setSelected(null);
        setAdding(false);
        setLocations([]);
        setMoveItem(null);
        setMoveSelection(null);
        setImage(null);
        setImagePreview(undefined);
      }
      grantRevision.current = page.share.revision;
      setRequiresSignIn(false);
      setView(page);
      setItems((previous) => {
        if (grantChanged) return page.items;
        const byId = new Map(previous.map((item) => [item.id, item]));
        const next = page.items.map((item) => {
          const existing = byId.get(item.id);
          // Keep the loaded image only while the authorized item is unchanged.
          return existing &&
            existing.revision === item.revision &&
            existing.keywords === item.keywords &&
            existing.qty === item.qty &&
            existing.locationId === item.locationId &&
            existing.location === item.location &&
            existing.imageAvailable === item.imageAvailable
            ? existing
            : item;
        });
        return next.length === previous.length &&
          next.every((item, index) => item === previous[index])
          ? previous
          : next;
      });
      setCursor(page.nextCursor);
      imageRequests.current.clear();
      setError('');
      setSelected((previous) =>
        previous?.id === activeItemId && !activeItemIncluded ? null : previous,
      );
    } catch (failure) {
      if (alive.current && epoch.current === generation) {
        if (
          (failure as { code?: string }).code === 'functions/unauthenticated' ||
          (failure as { code?: string }).code === 'unauthenticated'
        )
          setRequiresSignIn(true);
        setView(undefined);
        setItems([]);
        setCursor(undefined);
        setSelected(null);
        setAdding(false);
        setLocations([]);
        setMoveItem(null);
        setMoveSelection(null);
        setImage(null);
        setImagePreview(undefined);
        setError('This share is unavailable or your access has changed.');
      }
    } finally {
      if (alive.current && epoch.current === generation) setLoading(false);
    }
  }, [scope]);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  const requestImage = useCallback(
    (item: SharedItem) => (_priority: number) => {
      if (
        !item.imageAvailable ||
        item.image ||
        imageRequests.current.has(item.id)
      )
        return () => {};
      imageRequests.current.add(item.id);
      const generation = epoch.current;
      void readShared('image', { ...scope, itemId: item.id })
        .then((result) => {
          if (!alive.current || epoch.current !== generation) return;
          const uri = `data:${result.mimeType};base64,${result.base64}`;
          setItems((previous) =>
            previous.map((value) =>
              value.id === item.id ? { ...value, image: uri } : value,
            ),
          );
          setSelected((previous) =>
            previous?.id === item.id ? { ...previous, image: uri } : previous,
          );
        })
        .catch(() => {
          imageRequests.current.delete(item.id);
        });
      return () => {};
    },
    [scope],
  );
  const open = async (item: SharedItem | null) => {
    setSelected(item);
    setAdding(!item);
    setKeywords(item?.keywords || '');
    setQty(item?.qty ?? '');
    setLocation(item?.locationId || '');
    setImage(null);
    setImageCleared(false);
    if (item) requestImage(item)(1);
    if (view?.capabilities.showLocation && view.capabilities.canEdit) {
      try {
        const generation = epoch.current;
        const result = await sharedLocations(scope);
        if (alive.current && epoch.current === generation) setLocations(result);
      } catch {
        setError('Unable to load permitted locations.');
      }
    }
  };
  const close = () => {
    setSelected(null);
    setAdding(false);
    setImage(null);
    pendingMutation.current = undefined;
  };
  const editorGuard = useUnsavedChangesGuard({
    open: Boolean(selected || adding),
    pending: saving,
    dirty: Boolean(
      adding ||
      (selected &&
        (keywords !== selected.keywords ||
          qty !== (selected.qty ?? '') ||
          location !== (selected.locationId || '') ||
          image ||
          imageCleared)),
    ),
    onClose: close,
    onDiscard: () => {},
    scopeKey: 'shared.item',
  });
  const mutationId = (key: string) => {
    if (pendingMutation.current?.key !== key)
      pendingMutation.current = { key, operationId: createOperationId() };
    return pendingMutation.current.operationId;
  };
  const save = async () => {
    if (!view || saving) return false;
    setSaving(true);
    setError('');
    try {
      const chosen = locations.find((entry) => entry.id === location);
      const fields = {
        ...(adding || keywords !== selected?.keywords ? { keywords } : {}),
        ...(view.capabilities.showQuantity &&
        (adding || Number(qty) !== selected?.qty)
          ? { qty: Number(qty) }
          : {}),
        ...(view.capabilities.showLocation &&
        (adding || location !== selected?.locationId)
          ? { locationId: chosen?.id, location: chosen?.name }
          : {}),
      };
      if (
        view.capabilities.showQuantity &&
        (!Number.isSafeInteger(Number(qty)) || Number(qty) < 0 || qty === '')
      )
        throw new Error('Enter a valid quantity.');
      if (view.capabilities.showLocation && !chosen)
        throw new Error('Choose a location.');
      if (!adding && !image && !imageCleared && !Object.keys(fields).length) {
        close();
        return true;
      }
      const operationId = mutationId(
        JSON.stringify([
          adding,
          selected?.id,
          fields,
          imageCleared,
          image?.name,
          image?.lastModified,
        ]),
      );
      if (image)
        await imageOperationService.submit(
          {
            ...scope,
            operationId,
            ...(adding
              ? {
                  action: 'create-item' as const,
                  fields: fields as {
                    keywords: string;
                    qty: number;
                    location: string;
                    locationId: string;
                  },
                }
              : {
                  action: 'replace-image' as const,
                  itemIds: [selected!.id],
                  expectedRevisions: { [selected!.id]: selected!.revision },
                  fields,
                }),
          },
          image,
        );
      else if (adding) await create({ ...scope, operationId, item: fields });
      else
        await update({
          ...scope,
          operationId,
          items: [
            {
              id: selected!.id,
              expectedRevision: selected!.revision,
              updates: {
                ...fields,
                ...(imageCleared ? { image: '', storagePath: '' } : {}),
              },
            },
          ],
        });
      close();
      await refresh();
      return true;
    } catch (failure) {
      setError(
        failure instanceof Error ? failure.message : 'Unable to save item.',
      );
      return false;
    } finally {
      setSaving(false);
    }
  };
  const trash = async () => {
    if (
      !selected ||
      !view?.capabilities.canTrash ||
      !(await confirm(
        'Move this item to the source collection’s bin?',
        'Trash item',
      ))
    )
      return;
    setSaving(true);
    try {
      await update({
        ...scope,
        operationId: mutationId(`trash:${selected.id}`),
        items: [
          {
            id: selected.id,
            expectedRevision: selected.revision,
            updates: { deleted: true },
          },
        ],
      });
      close();
      await refresh();
    } catch (failure) {
      setError(
        failure instanceof Error ? failure.message : 'Unable to trash item.',
      );
    } finally {
      setSaving(false);
    }
  };
  const canEdit = Boolean(view?.capabilities.canEdit);
  const showLocation = Boolean(view?.capabilities.showLocation);
  const showQuantity = Boolean(view?.capabilities.showQuantity);
  const scopeKey = JSON.stringify([
    'shared',
    scope.collectionId,
    scope.shareId,
    user?.uid || 'anonymous',
    view?.share.revision,
  ]);
  const inventoryItems = useMemo(
    () =>
      items.map((item) => ({
        ...item,
        collectionId: scope.collectionId,
        image: item.image || '',
        location: showLocation ? item.location || '' : '',
        qty: showQuantity ? (item.qty ?? 0) : 0,
        createdAt: '',
        requestImage: requestImage(item),
      })),
    [items, scope.collectionId, showLocation, showQuantity, requestImage],
  );
  const filterLocations = useMemo(
    () =>
      showLocation
        ? Array.from(
            new Map(
              items
                .filter((item) => item.locationId && item.location)
                .map((item) => [
                  item.locationId!,
                  {
                    id: item.locationId!,
                    collectionId: scope.collectionId,
                    name: item.location!,
                    description: '',
                    normalizedName: item.location!.toLowerCase(),
                    createdAt: '',
                  },
                ]),
            ).values(),
          )
        : [],
    [items, showLocation, scope.collectionId],
  );
  const pendingBulk = useRef(
    new Map<
      string,
      {
        items?: Array<{
          id: string;
          expectedRevision: number;
          updates: Record<string, unknown>;
        }>;
        expectedRevisions?: Record<string, number>;
      }
    >(),
  );
  useEffect(() => {
    pendingBulk.current.clear();
  }, [scopeKey]);
  const bulkTransport: BulkActionTransport = {
    updateItems: async (updates, operationId) => {
      if (!canEdit) throw new Error('This share is read-only.');
      let intent = pendingBulk.current.get(operationId);
      if (!intent) {
        intent = {
          items: updates.map((entry) => {
            const item = items.find((item) => item.id === entry.id);
            if (!item) throw new Error('This item is no longer shared.');
            return { ...entry, expectedRevision: item.revision };
          }),
        };
        pendingBulk.current.set(operationId, intent);
      }
      await update({ ...scope, operationId, items: intent.items });
      pendingBulk.current.delete(operationId);
      await refresh();
    },
    applyImage: async (itemIds, image, operationId) => {
      if (!canEdit) throw new Error('This share is read-only.');
      let intent = pendingBulk.current.get(operationId);
      if (!intent) {
        intent = {
          expectedRevisions: Object.fromEntries(
            itemIds.map((id) => {
              const item = items.find((item) => item.id === id);
              if (!item) throw new Error('This item is no longer shared.');
              return [id, item.revision];
            }),
          ),
        };
        pendingBulk.current.set(operationId, intent);
      }
      await imageOperationService.submit(
        {
          ...scope,
          action: 'replace-image',
          itemIds,
          expectedRevisions: intent.expectedRevisions,
          operationId,
        },
        image,
      );
      pendingBulk.current.delete(operationId);
      await refresh();
    },
  };
  if (requiresSignIn && user === null)
    return <Login sessionError="Sign in to use this editor link." />;
  if (requiresSignIn && user && !user.emailVerified)
    return (
      <EmailVerification
        email={user.email}
        onRefresh={async () => {
          const verified = await refreshUser();
          if (verified) await refresh();
          return verified;
        }}
        onResend={authService.resendVerification}
        onSignOut={authService.signOut}
      />
    );
  const effectiveSort =
    (!showLocation && sort.startsWith('location-')) ||
    (!showQuantity && sort.startsWith('quantity-'))
      ? 'keywords-asc'
      : sort;
  return (
    <AppShell removeMargins={ui.preferences.removeMargins}>
      <InventoryItemsPage
        key={scopeKey}
        identity={{
          userId: user?.uid || 'anonymous',
          collectionId: scope.collectionId,
          scopeKey,
        }}
        shared
        tagState={emptyTags}
        bulkTransport={bulkTransport}
        items={inventoryItems}
        locations={filterLocations}
        loading={loading && !view}
        inventoryStatus={loading ? (view ? 'updating' : 'loading') : undefined}
        error={error || null}
        phoneLayout={phoneLayout}
        stickyTop={0}
        removeMargins={ui.preferences.removeMargins}
        view={ui.preferences.itemView}
        onViewChange={ui.setItemView}
        sort={effectiveSort}
        onSortChange={setSort}
        cardSize={ui.preferences.cardSize}
        readOnly={!canEdit}
        disableBulkActions={!canEdit}
        canDelete={Boolean(view?.capabilities.canTrash)}
        additionsBlocked={!view?.capabilities.canAdd}
        showLocation={showLocation}
        showQuantity={showQuantity}
        incompleteResults={Boolean(cursor)}
        onRefresh={() => {
          void refresh();
        }}
        onOpenItem={(item) => {
          const selectedItem = items.find((entry) => entry.id === item.id);
          if (selectedItem) void open(selectedItem);
        }}
        onAddItem={() => {
          void open(null);
        }}
        updateLastUsedLocation={async () => {}}
        locationFilterScope={{
          collectionIds: [scope.collectionId],
          unavailableCollectionIds: [],
          completeCollectionIds: cursor ? [] : [scope.collectionId],
        }}
        onRequestMove={
          canEdit
            ? (selectedItems, onComplete) =>
                setMoveSelection({ items: selectedItems, onComplete })
            : undefined
        }
        shellHeader={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              py: 2,
            }}
          >
            <Typography
              variant="h5"
              sx={{ overflowWrap: 'anywhere', minWidth: 0 }}
            >
              {view ? sharedTitle(view.share) : 'Shared inventory'}
            </Typography>
            <Button href="/items" sx={{ flexShrink: 0 }}>
              My inventory
            </Button>
          </Box>
        }
        pageMenuActions={[
          ...(view?.capabilities.canAdd
            ? [
                {
                  label: 'Transfer items in',
                  icon: <SyncIcon />,
                  onClick: () => setMoveItem('in'),
                },
              ]
            : []),
          {
            label: 'Display settings',
            icon: <SettingsIcon />,
            onClick: () => setDisplayOpen(true),
          },
        ]}
      />
      {phoneLayout && view?.capabilities.canAdd && (
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            p: 1.5,
            bgcolor: 'background.paper',
            boxShadow: surfaceShadows.footer,
            zIndex: 3,
          }}
        >
          <Button
            fullWidth
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              void open(null);
            }}
          >
            Add item
          </Button>
        </Box>
      )}
      {cursor && (
        <Stack spacing={1} alignItems="center" sx={{ py: 2 }}>
          <Typography variant="caption">
            Search, filters and sorting cover loaded items.
          </Typography>
          <Button
            disabled={loading}
            onClick={() => {
              setLoading(true);
              const generation = epoch.current;
              void readShared('listItems', { ...scope, cursor })
                .then((result) => {
                  if (alive.current && epoch.current === generation) {
                    setItems((previous) => [...previous, ...result.items]);
                    setCursor(result.nextCursor);
                  }
                })
                .catch(() => {
                  setError(
                    'Unable to load more items. Refresh to check access.',
                  );
                })
                .finally(() => {
                  if (alive.current && epoch.current === generation)
                    setLoading(false);
                });
            }}
          >
            Load more
          </Button>
        </Stack>
      )}
      <ResponsiveDialog
        open={displayOpen}
        onClose={() => setDisplayOpen(false)}
        aria-labelledby="shared-display-title"
      >
        <DialogHeader
          id="shared-display-title"
          title="Display settings"
          onClose={() => setDisplayOpen(false)}
        />
        <Box sx={{ p: 3 }}>
          <InventoryDensityControl
            cardSize={ui.preferences.cardSize}
            onCardSizeChange={ui.setCardSize}
            removeMargins={ui.preferences.removeMargins}
            onRemoveMarginsChange={ui.setRemoveMargins}
          />
        </Box>
      </ResponsiveDialog>
      {view && (selected || adding) && (
        <ItemDetailsModal
          open
          onClose={() => {
            void editorGuard.requestClose();
          }}
          selectedItem={
            {
              ...(selected || { id: 'new', keywords: '' }),
              image: imagePreview || selected?.image || '',
              createdAt: '',
            } as Item
          }
          image={imagePreview || (!imageCleared && selected?.image) || null}
          keywords={keywords}
          setKeywords={setKeywords}
          location={
            locations.find((entry) => entry.id === location)?.name ||
            (!view.capabilities.canEdit && selected?.location) ||
            location
          }
          setLocation={(value) =>
            setLocation(
              locations.find(
                (entry) => entry.name === value || entry.id === value,
              )?.id || value,
            )
          }
          qty={qty}
          setQty={setQty}
          handleUpdate={save}
          handleDeleteFromModal={() => {
            void trash();
          }}
          locations={locations.map((entry) => ({
            ...entry,
            normalizedName: entry.name.toLowerCase(),
            description: '',
            createdAt: '',
          }))}
          setShowAddLocationModal={() => {}}
          handleImageCapture={(file) => {
            setImage(file);
            setImageCleared(false);
          }}
          handleClearImage={async () => {
            setImage(null);
            setImageCleared(true);
            return true;
          }}
          compressionInProgress={saving}
          compressionProgress={0}
          handleDuplicate={() => {}}
          saveLocation={async () => false}
          deleteLocation={async () => false}
          readOnly={!view.capabilities.canEdit}
          canDelete={!adding && view.capabilities.canTrash}
          canDuplicate={false}
          canManageLocations={false}
          showMetadata={false}
          showLocation={view.capabilities.showLocation}
          showQuantity={view.capabilities.showQuantity}
          saveLabel={adding ? 'Add item' : 'Save'}
        />
      )}
      {view && moveSelection && (
        <SharedItemMoveDialog
          key={scopeKey}
          scope={scope}
          userId={user?.uid || ''}
          collectionName={view.share.collectionName}
          tagName={view.share.tagName}
          items={moveSelection.items}
          canTransferOut={view.capabilities.canTransferOut}
          onProgress={() => {
            void refresh();
          }}
          onClose={() => setMoveSelection(null)}
          onMoved={() => {
            moveSelection.onComplete();
            setMoveSelection(null);
            void refresh();
          }}
        />
      )}
      {moveItem && (
        <SharedMoveDialog
          collectionId={scope.collectionId}
          shareId={scope.shareId}
          linkToken={scope.linkToken}
          onClose={() => setMoveItem(null)}
          onMoved={() => {
            void refresh();
          }}
        />
      )}
    </AppShell>
  );
}
